import { useState } from 'react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import apiClient from '@/api/client';
import { useAdminAuth } from '@/store/useAdminAuth';

// ─── Erzwungener Passwortwechsel ─────────────────────────────────────────────
//
// Eine frisch aufgesetzte Installation legt das Betreiberkonto mit
// `mustChangePassword` an. Ohne diese Maske stünde man davor: angemeldet,
// aber jede Route antwortet mit 403 – und in der Konsole gäbe es keinen Weg
// heraus. Deshalb steht sie VOR dem Router, nicht darin.

export function PasswortAendernPage() {
  const anmelden = useAdminAuth((s) => s.anmelden);
  const abmelden = useAdminAuth((s) => s.abmelden);

  const [aktuell, setAktuell] = useState('');
  const [neu, setNeu] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const zuKurz = neu.length > 0 && neu.length < 8;
  const stimmtNicht = wiederholung.length > 0 && neu !== wiederholung;

  const absenden = async (e: React.FormEvent) => {
    e.preventDefault();
    if (neu !== wiederholung) {
      setFehler('Die beiden neuen Passwörter stimmen nicht überein.');
      return;
    }
    setFehler(null);
    setLaeuft(true);

    try {
      const { data } = await apiClient.post('/auth/change-password', {
        current_password: aktuell,
        new_password: neu,
      });
      // Der Server gibt ein frisches Token mit demselben Bereich zurück –
      // die Sitzung bleibt bestehen, nur ohne die Sperre.
      anmelden(data.user, data.accessToken, data.csrfToken);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Änderung fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-800 ring-1 ring-slate-700">
            <KeyRound className="h-5 w-5 text-slate-300" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Passwort ändern</h1>
            <p className="text-xs text-slate-400">Einmalig vor der ersten Nutzung</p>
          </div>
        </div>

        <form
          onSubmit={absenden}
          className="rounded-xl border border-slate-700 bg-slate-800 p-5 shadow-xl"
        >
          <p className="mb-4 text-xs leading-relaxed text-slate-400">
            Dieses Konto erreicht jede Familie auf dieser Installation. Solange
            das Startpasswort gilt, bleibt die Konsole gesperrt.
          </p>

          <label htmlFor="aktuell" className="block text-xs font-medium text-slate-300">
            Aktuelles Passwort
          </label>
          <input
            id="aktuell"
            type="password"
            required
            autoComplete="current-password"
            value={aktuell}
            onChange={(e) => setAktuell(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          <label htmlFor="neu" className="mt-4 block text-xs font-medium text-slate-300">
            Neues Passwort
          </label>
          <input
            id="neu"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={neu}
            onChange={(e) => setNeu(e.target.value)}
            aria-describedby="neu-hinweis"
            className="mt-1.5 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p
            id="neu-hinweis"
            className={`mt-1 text-[11px] ${zuKurz ? 'text-amber-400' : 'text-slate-500'}`}
          >
            Mindestens 8 Zeichen.
          </p>

          <label htmlFor="wdh" className="mt-3 block text-xs font-medium text-slate-300">
            Neues Passwort wiederholen
          </label>
          <input
            id="wdh"
            type="password"
            required
            autoComplete="new-password"
            value={wiederholung}
            onChange={(e) => setWiederholung(e.target.value)}
            className={`mt-1.5 w-full rounded-lg border bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 ${
              stimmtNicht
                ? 'border-amber-600 focus:border-amber-500 focus:ring-amber-500'
                : 'border-slate-600 focus:border-blue-500 focus:ring-blue-500'
            }`}
          />
          {stimmtNicht && (
            <p className="mt-1 text-[11px] text-amber-400">Stimmt noch nicht überein.</p>
          )}

          {fehler && (
            <p className="mt-4 flex items-start gap-2 rounded-lg bg-red-950 p-2.5 text-xs leading-relaxed text-red-300 ring-1 ring-red-900">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {fehler}
            </p>
          )}

          <button
            type="submit"
            disabled={laeuft || zuKurz || stimmtNicht || !neu}
            className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {laeuft ? 'Wird gespeichert…' : 'Passwort ändern'}
          </button>
        </form>

        <button
          onClick={abmelden}
          className="mt-4 w-full text-center text-xs text-slate-500 hover:text-slate-300"
        >
          Abbrechen und abmelden
        </button>
      </div>
    </div>
  );
}
